import {
  DocumentData,
  DocumentSnapshot,
  FirestoreError,
  Query,
  QueryCompositeFilterConstraint,
  QueryConstraint,
  QueryDocumentSnapshot,
  QueryOrderByConstraint,
  QuerySnapshot,
  Timestamp,
  UpdateData,
  WithFieldValue,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from 'core/config/firebase';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';
import { FirestoreCollectionIDs } from 'core/constants/firestore-collection-ids';
import { IActor, IUpdateDataPayload } from './types';
import { v4 as uuid } from 'uuid';
import ApiService from './api.service';

const createService = <T, U>(collectionId: FirestoreCollectionIDs) => {
  const converter = <T>() => ({
    toFirestore: (dao: WithFieldValue<T>) => dao as DocumentData,
    fromFirestore: (snapshot: QueryDocumentSnapshot<DocumentData>) => snapshot.data() as T,
  });

  const collectionRef = collection(firestore, collectionId);

  const docRef = (uid: string) => doc(firestore, collectionId, uid).withConverter(converter<U>());

  const get = (uid: string) => {
    const docRef = doc(firestore, collectionId, uid).withConverter(converter<U>());
    return getDoc(docRef);
  };

  const set = async (dto: T & { uid?: string }, user: IActor) => {
    const uid = dto.uid ?? uuid();
    return setDoc(doc(firestore, collectionId, uid).withConverter(converter<T>()), {
      ...dto,
      uid,
      created: { at: Timestamp.now(), by: user },
      updated: { at: Timestamp.now(), by: user },
    });
  };

  const update = async (uid: string, update: UpdateData<T>, user: IActor) => {
    if (typeof update !== 'object') {
      throw new Error('Update data is required');
    }

    const updated = { at: Timestamp.now(), by: user };
    const docRef = doc(firestore, collectionId, uid).withConverter(converter<T>());
    return updateDoc(docRef, { ...update, updated });
  };

  const softDelete = (uid: string, user: IActor) => {
    return update(uid, { deleted: true } as UpdateData<T>, user);
  };

  const hardDelete = (uid: string) => {
    return deleteDoc(docRef(uid));
  };

  const getAll = (constraints: QueryConstraint[] = []) => {
    const q = query(collectionRef, ...constraints);
    return getDocs(q.withConverter(converter<T>()));
  };

  const bulkSet = async (daos: (T & { uid: string })[]) => {
    const batch = writeBatch(firestore);

    daos.forEach((dao) => {
      batch.set(doc(firestore, collectionId, dao.uid).withConverter(converter<T>()), dao);
    });

    return batch.commit();
  };

  const bulkUpdate = async (updates: { uid: string; dao: IUpdateDataPayload<T> }[]) => {
    const batch = writeBatch(firestore);
    updates.forEach((update) => {
      batch.update(doc(firestore, collectionId, update.uid).withConverter(converter<T>()), update.dao as any);
    });
    return batch.commit();
  };

  const bulkHardDelete = async (uids: string[]) => {
    const batch = writeBatch(firestore);
    uids.forEach((uid) => {
      batch.delete(doc(firestore, collectionId, uid).withConverter(converter<T>()));
    });
    return batch.commit();
  };

  const onDocSnapshot = (
    uid: string,
    resultHandler: (snap: DocumentSnapshot<U>) => void,
    errorHandler: (error: FirestoreError) => void
  ) => {
    return onSnapshot(
      docRef(uid),
      (snapShot: DocumentSnapshot<U>) => {
        resultHandler(snapShot);
      },
      (error: FirestoreError) => {
        errorHandler(error);
      }
    );
  };

  const onCollectionSnapshot = (
    resultHandler: (snap: QuerySnapshot<U>) => void,
    errorHandler: (error: FirestoreError) => void,
    constraints?: QueryConstraint[],
    compositeFilter?: {
      filter: QueryCompositeFilterConstraint;
      orderBy: QueryOrderByConstraint;
    }
  ) => {
    let q: Query<U> = query(collectionRef).withConverter(converter<U>());

    if (constraints && constraints.length > 0) {
      q = query(collectionRef, ...constraints).withConverter(converter<U>());
    } else if (compositeFilter) {
      q = query(collectionRef, compositeFilter.filter, compositeFilter.orderBy).withConverter(converter<U>());
    }

    return onSnapshot(
      q,
      (snapShot) => {
        resultHandler(snapShot);
      },
      (error) => {
        errorHandler(error);
      }
    );
  };

  const getCollectionCount = (constraints: QueryConstraint[] = []) => {
    const q = query(collectionRef, ...constraints).withConverter(converter<T>());
    return getCountFromServer(q);
  };

  const callFunction = async (data: any, functionName: FirebaseFunctionNames): Promise<string> => {
    return ApiService.callFunction(data, functionName);
  };

  return {
    converter,
    collectionRef,
    docRef,
    get,
    set,
    update,
    softDelete,
    hardDelete,
    getAll,
    bulkSet,
    bulkUpdate,
    bulkHardDelete,
    onDocSnapshot,
    onCollectionSnapshot,
    getCollectionCount,
    callFunction,
  };
};

export default createService;
